import axios from 'utils/axios';
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Backdrop, CircularProgress } from '@mui/material';

import { useDispatch } from 'store';
import { showErrorAlert } from 'utils/global-functions';

import CoachingLog from './CoachingLog';
import StandardSession from './StandardSession';
import { CalendarContext } from 'contexts/CalendarContext';

export default function Session() {
    const [session, setSession] = useState(null);
    const [loadingSession, setLoadingSession] = useState(undefined);
    const [substituteTeachers, setSubstituteTeachers] = useState([]);
    const [socialEmotionalSkills, setSocialEmotionalSkills] = useState([]);

    const { state } = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { setOpenSession } = useContext(CalendarContext);

    const closeSession = () => navigate(state?.callbackUrl ? state.callbackUrl : '/calendar');

    async function getSubstituteTeachers(signal) {
        try {
            const { data } = await axios.get('/Session/SubstituteTeachers', { signal });
            const teachers = data.map((t) => ({ label: t.name, id: t.id }));
            setSubstituteTeachers(teachers);
        } catch (err) {
            if (signal.aborted) return;
            console.error(err);
            showErrorAlert(dispatch, err);
        }
    }

    async function getSocialEmotionalSkills() {
        try {
            const { data } = await axios.get('/Session/GetSocialEmotionalSkills');
            setSocialEmotionalSkills(data);
        } catch (err) {
            console.error(err);
            showErrorAlert(dispatch, err);
        }
    }

    useEffect(() => {
        // Stopped when the page closes or the session fails to load, so its error can't show over the session's
        const substitutesRequest = new AbortController();

        async function getSession() {
            setLoadingSession(true);

            try {
                let sessionId = state?.session?.id;
                if (!sessionId) {
                    const urlPathParts = window.location.pathname.split('/');
                    sessionId = urlPathParts[urlPathParts.length - 1];
                }

                // The substitute list doesn't depend on the session, so it loads at the same time
                const substituteTeachersLoaded = getSubstituteTeachers(substitutesRequest.signal);
                const { data } = await axios.get(`/Session/${sessionId}`);
                await Promise.all([
                    data.service === 'Mentoring' ? getSocialEmotionalSkills() : Promise.resolve(),
                    substituteTeachersLoaded
                ]);
                setSession(data);
                setOpenSession(true);
            } catch (err) {
                substitutesRequest.abort();
                showErrorAlert(dispatch, err);
                setLoadingSession(false);
                closeSession();
            }
        }

        getSession();
        return () => substitutesRequest.abort();
    }, []);

    if (session) {
        return session?.sessionType === 1 ? (
            <CoachingLog session={session} closeSession={closeSession} />
        ) : (
            <StandardSession
                session={session}
                setSession={setSession}
                closeSession={closeSession}
                loadingSession={loadingSession}
                setLoadingSession={setLoadingSession}
                substituteTeachers={substituteTeachers}
                socialEmotionalSkills={socialEmotionalSkills}
            />
        );
    }

    return (
        <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open>
            <CircularProgress color="primary" />
        </Backdrop>
    );
}
